import type { ComponentProps, ReactNode } from "react"
import { useCallback, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconPlusOutline18 as Plus } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { IconButton } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { useHighlights } from "@/components/editor/editor-config"
import { RoleCheck } from "@/components/shared/role-check"
import { Tag } from "@/components/tag"
import { mutateOk, useZero } from "@/lib/zero"
import { useThreadContext } from "../../thread-context"
import { useLinkedMenu } from "./linked-menu-context"
import { ThreadTagSelect } from "./thread-tag-select"

type Props = ComponentProps<"div">

export function LinkedTags({ className, ...props }: Props) {
	const zero = useZero()
	const { thread } = useThreadContext()
	const { isMenuOpen, onMenuOpenChange } = useLinkedMenu()
	const tags = thread.tags

	const removeMarks = useCallback((id: string) => {
		const marks = document.querySelectorAll(`mark[data-id="${id}"]`)
		marks.forEach((mark) => {
			mark.replaceWith(mark.innerHTML)
		})
	}, [])

	if (tags.length > 0) {
		return null
	}

	return (
		<div className={cn("", className)} {...props}>
			<div className="flex items-center">
				<RoleCheck not={["VIEWER"]}>
					<ThreadTagSelect
						tagIds={tags.map((t) => t.id)}
						side="left"
						modal={false}
						closeOnSelect
						isOpen={isMenuOpen("tag")}
						onOpenChange={onMenuOpenChange("tag")}
						onChange={async (tagId) => {
							const existingTagIds = tags.map((t) => t.id)
							if (existingTagIds.includes(tagId)) {
								// unlink
								removeMarks(tagId)
							}
							// it toggles linking of tag
							await mutateOk(
								zero.mutate(
									mutators.tags.linkTag({
										threadId: thread.id,
										tagId: tagId,
									}),
								),
							)
						}}
					/>
				</RoleCheck>
			</div>
		</div>
	)
}

/** The thread's tags, then any suggested tags, then the add button. */
export const ThreadTagsList = ({
	suggestions,
}: {
	suggestions?: ReactNode
}) => {
	const zero = useZero()
	const { thread } = useThreadContext()
	const { isMenuOpen, onMenuOpenChange } = useLinkedMenu()

	const tags = thread.tags
	const [tagGroups] = useQuery(queries.tagGroups())
	const { hoveredIds, setHoveredIds } = useHighlights()

	const tagGroupMap = useMemo(() => {
		const map = new Map<string, string>()
		tagGroups.forEach((group) => {
			map.set(group.id, group.color)
		})
		return map
	}, [tagGroups])

	const removeMarks = useCallback((id: string) => {
		const marks = document.querySelectorAll(`mark[data-id="${id}"]`)
		marks.forEach((mark) => {
			mark.replaceWith(mark.innerHTML)
		})
	}, [])

	// With no tags the Tag button below the list adds them, so only suggestions show.
	if (tags.length === 0) {
		return suggestions ?? null
	}

	return (
		<>
			{tags.map((tag) => (
				<Tag
					showLinkButton
					key={tag.id}
					tag={tag}
					groupColor={tagGroupMap.get(tag.tagGroupId)}
					onMouseEnter={() => {
						setHoveredIds([tag.id])
					}}
					onMouseLeave={() => {
						setHoveredIds([])
					}}
					onClose={async () => {
						removeMarks(tag.id)
						await mutateOk(
							zero.mutate(
								mutators.tags.linkTag({
									threadId: thread.id,
									tagId: tag.id,
								}),
							),
						)
					}}
					className={cn(hoveredIds.includes(tag.id) && "bg-opacity-15")}
				/>
			))}
			{suggestions}
			<RoleCheck not={["VIEWER"]}>
				<ThreadTagSelect
					tagIds={tags.map((t) => t.id)}
					side="left"
					variant="ghost"
					modal={false}
					closeOnSelect
					isOpen={isMenuOpen("tag")}
					onOpenChange={onMenuOpenChange("tag")}
					onChange={async (tagId) => {
						const existingTagIds = tags.map((t) => t.id)
						if (existingTagIds.includes(tagId)) {
							removeMarks(tagId)
						}
						await mutateOk(
							zero.mutate(
								mutators.tags.linkTag({
									threadId: thread.id,
									tagId: tagId,
								}),
							),
						)
					}}
				>
					<IconButton
						Icon={Plus}
						variant="ghost"
						size="sm"
						className="text-gray-500 dark:text-gray-400"
					/>
				</ThreadTagSelect>
			</RoleCheck>
		</>
	)
}
